// The retirement path for a stale bundle. Detecting staleness is not the same
// as delivering the fix: a single-page app keeps executing the bundle it
// loaded, so until now a frontend fix reached an open tab only when its user
// happened to click the banner's Reload. A tab left open polls on, and keeps
// reporting bugs that were fixed days earlier. This reloads such a page by
// itself, but only once its tab has been out of sight long enough that nothing
// on screen can be lost.

import { useEffect } from "react";
import { useStaleServerVersion } from "../api/build-version";

/** How long the tab must stay continuously hidden before the page retires
 * itself. Long enough that a glance at another window costs no in-progress
 * dialog state, short enough that a deploy drains open tabs in minutes. */
const HIDDEN_GRACE_MS = 5 * 60 * 1000;

/** The server version this tab has already auto-reloaded for. sessionStorage
 * survives a reload in the same tab and dies with the tab, so a page that is
 * still stale after reloading — a cached index.html, a response fetched
 * mid-deploy — stops here and falls back to the banner instead of looping. */
const AUTO_RELOADED_VERSION_KEY = "odi.auto-reloaded-build";

/** Reloads a stale page once its tab has been continuously hidden for the
 * grace period, at most once per server version. A visible tab is left alone:
 * it has a user in front of it, and the banner is already there. */
export function useStaleBuildAutoReload(): void {
	const staleServerVersion = useStaleServerVersion();

	useEffect(() => {
		if (staleServerVersion === null) return;
		if (sessionStorage.getItem(AUTO_RELOADED_VERSION_KEY) === staleServerVersion) {
			return;
		}

		let pendingReload: number | undefined;

		const cancelPendingReload = (): void => {
			if (pendingReload !== undefined) {
				window.clearTimeout(pendingReload);
				pendingReload = undefined;
			}
		};

		const onVisibilityChange = (): void => {
			// Coming back into view cancels the pending reload; going out of
			// view starts the grace period over.
			cancelPendingReload();
			if (!document.hidden) return;
			pendingReload = window.setTimeout(() => {
				sessionStorage.setItem(AUTO_RELOADED_VERSION_KEY, staleServerVersion);
				window.location.reload();
			}, HIDDEN_GRACE_MS);
		};

		// The flag can latch while the tab is already hidden — polls keep
		// running there — so start from the current state, not the next change.
		onVisibilityChange();
		document.addEventListener("visibilitychange", onVisibilityChange);
		return () => {
			document.removeEventListener("visibilitychange", onVisibilityChange);
			cancelPendingReload();
		};
	}, [staleServerVersion]);
}
